/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2020 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

/**
 * Filename: break-prepostroll-asset-service.ts
 *
 * Created by kyang on 10/28/20.
 */

// This service is an implementation for BrandingAssetService.
// Components should invoke the abstract BrandingAssetService, not this specific implementation.

angular.module('prism-ui.components.branding').service('BreakPrePostRollAssetService', function (
    this: JS5Object,
    _: JS5Object,
    $q: JS5Object,
    BrandingAssetService: JS5Object,
    BrandingViewService: JS5Object,
    BreakPrePostRollResourceFactory: JS5Object,
    MediaProfileResourceFactory: JS5Object,
    PrePostRollResourceFactory: JS5Object,
) {

    //==========================//
    // Branding Asset Container //
    //==========================//

    this.getBrandingAssetContainer = (containerId: string) => {
        return BreakPrePostRollResourceFactory.get({containerId}).$promise;
    };

    this.parseBrandingAssetContainer = (brandingConfig: JS5Object, brandingAssetContainer: JS5Object) => {
        brandingAssetContainer.breakPrePostRolls = BrandingAssetService.filterBrandingAssets(brandingConfig, brandingAssetContainer.breakPrePostRolls, {anySeasonVolume: true});
    };

    this.updateBrandingAssetContainer = (brandingConfig: JS5Object, brandingAssetContainer: JS5Object) => {
        return BreakPrePostRollResourceFactory.update({
            containerId: brandingAssetContainer.id
        }, brandingAssetContainer).$promise;
    };


    //=======//
    // Asset //
    //=======//

    const createEmptyRootBrandingAsset = (brandingConfig: JS5Object, acceptProfile: JS5Object) => {
        let rootAsset: JS5Object = {
            acceptProfile: acceptProfile,
            preRoll: {
                acceptProfile: acceptProfile,
                audios: [],
                texts: [],
            },
            postRoll: {
                acceptProfile: acceptProfile,
                audios: [],
                texts: [],
            },
        };
        if (brandingConfig.seasonId) {
            rootAsset.seasonId = brandingConfig.seasonId;
        } else if (brandingConfig.volumeId) {
            rootAsset.volumeId = brandingConfig.volumeId;
        }
        return rootAsset;
    };

    this.getRootBrandingAsset = (brandingConfig: JS5Object, container: JS5Object, acceptProfile: JS5Object) => {
        let breakPrePostRolls = BrandingAssetService.filterBrandingAssets(brandingConfig, container.breakPrePostRolls);
        let rootAsset = _.find(breakPrePostRolls, {
            acceptProfile: {id: acceptProfile.id}
        });
        if (rootAsset) {
            rootAsset.preRoll.audios = _.get(rootAsset.preRoll, 'audios', []);
            rootAsset.preRoll.texts = _.get(rootAsset.preRoll, 'texts', []);
            rootAsset.postRoll.audios = _.get(rootAsset.postRoll, 'audios', []);
            rootAsset.postRoll.texts = _.get(rootAsset.postRoll, 'texts', []);
            return rootAsset;
        }
        return createEmptyRootBrandingAsset(brandingConfig, acceptProfile);
    };

    this.validateNonTextAsset = (acceptProfileId: number, asset: JS5Object, virtualPath: string) => {
        return MediaProfileResourceFactory.validatePrePostRoll({
            path: virtualPath,
            profileId: acceptProfileId
        }).$promise.then((validationResult: JS5Object) => {
            return _.get(validationResult, `${acceptProfileId}.failures`);
        });
    };

    const updateSubAssets = (brandingConfig: JS5Object, acceptProfile: JS5Object) => {
        return this.getBrandingAssetContainer(brandingConfig.id)
            .then((container: JS5Object) => {
                return this.getRootBrandingAsset(brandingConfig, container, acceptProfile);
            })
            .then((savedBreakPrePostRoll: JS5Object) => {
                return $q.all([
                    ingestSubAssets(savedBreakPrePostRoll.preRoll),
                    ingestSubAssets(savedBreakPrePostRoll.postRoll),
                ]).then(() => {
                    return savedBreakPrePostRoll;
                });
            });
    };

    const ingestSubAssets = (videoAsset: JS5Object) => {
        let audioAssetPromises: JS5Promise[] = [];
        let textAssetPromises: JS5Promise[] = [];
        _.forEach(videoAsset.audios, (audioAsset: JS5Object) => {
            if (!audioAsset.assetId) {
                audioAssetPromises.push(PrePostRollResourceFactory.updateAssetData({
                    subTypeKey: 'audios',
                    subTypeId: audioAsset.id,
                }, audioAsset).$promise);
            }
        });
        _.forEach(videoAsset.texts, (textAsset: JS5Object) => {
            if (!textAsset.assetId) {
                textAssetPromises.push(PrePostRollResourceFactory.updateAssetData({
                    subTypeKey: 'texts',
                    subTypeId: textAsset.id,
                }, textAsset).$promise);
            }
        });

        return $q.all({
            audios: $q.all(audioAssetPromises),
            texts: $q.all(textAssetPromises),
        });
    };

    this.createRootBrandingAsset = (brandingConfig: JS5Object, rootAsset: JS5Object) => {
        // There is no /assets endpoint, so update the container instead
        return BreakPrePostRollResourceFactory.createAsset({containerId: brandingConfig.id}, rootAsset).$promise
            .then(() => {
                return updateSubAssets(brandingConfig, rootAsset.acceptProfile);
            });
    };

    this.updateRootBrandingAsset = (brandingConfig: JS5Object, rootAsset: JS5Object) => {
        return BreakPrePostRollResourceFactory.updateAsset({assetId: rootAsset.id}, rootAsset).$promise
            .then(() => {
                return updateSubAssets(brandingConfig, rootAsset.acceptProfile);
            });
    };


    //=========//
    // Proxies //
    //=========//

    this.hasVideoAssetProxyStatus = (brandingConfig: JS5Object, rootAsset: JS5Object) => {
        return (
            !BrandingAssetService.assetWasIngested(rootAsset.preRoll)
            || !BrandingAssetService.assetWasIngested(rootAsset.postRoll)
        );
    };

    const getVideoAssetProxyStatus = (updatedVideoAsset: JS5Object) => {
        if (_.get(updatedVideoAsset, 'proxyId')) {
            return BrandingAssetService.ProxyStatus.PROXY_COMPLETE;
        } else if (_.get(updatedVideoAsset, 'ingestInfo.workflowStatus') === BrandingAssetService.ProxyStatus.PROXY_STALLED) {
            return BrandingAssetService.ProxyStatus.PROXY_STALLED;
        }
        return BrandingAssetService.ProxyStatus.PROXY_RUNNING;
    };

    this.updateVideoAssetProxyStatus = (brandingConfig: JS5Object, rootAsset: JS5Object, intervalData: JS5Object) => {
        return this.getBrandingAssetContainer(brandingConfig.id)
            .then((updatedContainer: JS5Object) => {
                let updatedAsset = this.getRootBrandingAsset(brandingConfig, updatedContainer, rootAsset.acceptProfile);
                let preRollStatus = getVideoAssetProxyStatus(updatedAsset.preRoll);
                let postRollStatus = getVideoAssetProxyStatus(updatedAsset.postRoll);
                if (preRollStatus !== _.get(intervalData, 'prevPreRollStatus', BrandingAssetService.ProxyStatus.PROXY_RUNNING)) {
                    BrandingViewService.showVideoIngestStatusNotifications(brandingConfig, updatedAsset.preRoll, preRollStatus, '- PreRoll ');
                }
                if (postRollStatus !== _.get(intervalData, 'prevPostRollStatus', BrandingAssetService.ProxyStatus.PROXY_RUNNING)) {
                    BrandingViewService.showVideoIngestStatusNotifications(brandingConfig, updatedAsset.postRoll, postRollStatus, '- PostRoll ');
                }

                intervalData.prevPreRollStatus = preRollStatus;
                intervalData.prevPostRollStatus = postRollStatus;
                if (preRollStatus === BrandingAssetService.ProxyStatus.PROXY_COMPLETE && postRollStatus === BrandingAssetService.ProxyStatus.PROXY_COMPLETE) {
                    return BrandingAssetService.ProxyStatus.PROXY_COMPLETE;
                } else if (preRollStatus !== BrandingAssetService.ProxyStatus.PROXY_RUNNING && postRollStatus !== BrandingAssetService.ProxyStatus.PROXY_RUNNING) {
                    return BrandingAssetService.ProxyStatus.PROXY_STALLED;
                }
                return BrandingAssetService.ProxyStatus.PROXY_RUNNING;
            });
    };
});


//=================//
// Lifecycle Hooks //
//=================//

// Load this service when app is initialized
angular.module('prism-ui.components.branding').run((
    BrandingAssetService: JS5Object,
    BreakPrePostRollAssetService: JS5Object,
) => {
    BrandingAssetService.registerAssetServiceImplementation('breakPrePostRolls', BreakPrePostRollAssetService);
});
